import { AfterViewInit, ChangeDetectorRef, Component, Inject, NgZone, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation, inject } from '@angular/core';
import { of, Subject } from 'rxjs';
import { User } from 'src/app/core/interfaces/user';
import { SessionService } from 'src/app/core/services/session.service';
import { UserService } from 'src/app/core/services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { LANGUAGES } from 'src/app/core/constants/languages';
import { environment } from 'src/environments/environment';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LoggerService } from 'src/app/core/services/logger.service';
import { LogEvent } from 'src/app/core/enums/log-event';
import { Languagei18n } from 'src/app/core/enums/languages';
import { LIVECHAT_VERSION } from 'src/app/shared/utils/livechat_version';
import { UserNotificationStateService } from 'src/app/core/services/notifications/user-notification-state.service';
import { combineLatest, map, switchMap, take, takeUntil, tap } from 'rxjs';
import { ResponsivenessService } from '@core/services/responsiveness.service';
import { WINDOW_INJECTION_TOKEN } from '@core/constants/injection-tokens';
import { LiveChatStateService } from 'src/app/modules/live-chat/services/live-chat-state.service';
import { FeatureToggleService } from '@core/services/feature-toggle.service';
import { FeatureToggles } from '@core/enums/feature-toggles.enum';
import { SocketService } from 'src/app/core/services/socket-service/socket.service';
import { ConnectionType } from 'src/app/core/services/socket-service/websocket-service.types';
import { AskDropdownDirective, ThemeService } from 'asksuite-citrus';
import { FilterService } from 'src/app/modules/live-chat/services/filter.service';
import { BeamerService } from '@core/services/beamer.service';
import { KanbanOverlayService } from 'src/app/modules/live-chat/services/kanban-overlay.service';
import { PipelineService } from 'src/app/modules/live-chat/services/pipeline.service';
import { CompanyService } from '@core/services/company.service';
import { Pipeline } from 'src/app/modules/live-chat/interfaces/pipeline.model';
import { PhoneService } from 'src/app/modules/live-chat/services/phone.service';
import { OpenedByUrlAccessService } from 'src/app/modules/live-chat/services/opened-by-url-access.service';

export interface Avatar {
  name: string;
  image: string;
}

export type UserStatus = 'online' | 'paused';
export type SubmenuType = 'status' | 'language' | 'theme' | null;

export const STATUS_OPTIONS = [
  { value: 'online' as UserStatus, label: 'STATUS.ONLINE' },
  { value: 'paused' as UserStatus, label: 'STATUS.PAUSED' },
];

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(AskDropdownDirective) optionsDropdown!: AskDropdownDirective;

  user?: User;
  LANGUAGES = LANGUAGES;
  STATUS_OPTIONS = STATUS_OPTIONS;
  theme: 'dark' | 'light' = 'light';
  currentStatus: UserStatus = 'online';
  statusSynced = false;
  updatingStatus = false;
  pendingStatus: UserStatus | null = null;
  activeSubmenu: SubmenuType = null;
  private submenuHoverTimeout: ReturnType<typeof setTimeout> | null = null;
  private submenuLeaveTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly SUBMENU_HOVER_DELAY = 150;
  private readonly destroy$ = new Subject<void>();
  private readonly SUBMENU_LEAVE_DELAY = 100;
  private livechatVersion = inject(LIVECHAT_VERSION);
  protected filterForm!: FormGroup;
  protected avatar!: Avatar;
  protected showPhoneApp = false;
  protected showNewMenu = false;
  protected readonly defaultSmallLogoPath = 'assets/img/asksuite-logo.svg';
  protected readonly christmasSmallLogoPath = 'assets/img/asksuite-logo-natal-2025.svg';
  protected readonly shouldShowChristmasLogo = this.isChristmasSeason();
  protected readonly smallLogoSrc = this.shouldShowChristmasLogo ? this.christmasSmallLogoPath : this.defaultSmallLogoPath;

  public isScreenSmall$ = this.responsivenessService
    .isScreenLarge$()
    .pipe(map((isLarge) => isLarge));

  get avatarUrl() {
    return this.user?.urlAvatar || '';
  }

  get name() {
    return this.user?.name || '';
  }

  get firstName() {
    return this.name.split(' ')[0];
  }

  get version() {
    return this.livechatVersion ? `v${this.livechatVersion}` : '';
  }

  get isSomeDropdownOpen(): boolean {
    return this.optionsDropdown?.isRendered || false;
  }

  get numberNotification(): string {
    if(document.getElementsByClassName("beamer_icon noBouncy active")[0]){
      return document.getElementsByClassName("beamer_icon noBouncy active")[0]?.innerHTML || '0';
    } else {
      return ''
    }
  }

  get currentLanguageLabel(): string {
    const currentLang = this.filterForm?.get('language')?.value;
    if (currentLang?.label) {
      return currentLang.label;
    }
    const storedLang = localStorage.getItem('language');
    const found = LANGUAGES.find(l => l.value === storedLang);
    return found?.label || 'Português';
  }

  get currentStatusLabel(): string {
    const statusOption = STATUS_OPTIONS.find(s => s.value === this.currentStatus);
    return this.translateService?.instant(statusOption?.label || 'STATUS.ONLINE') || 'Online';
  }

  protected showKanban$ = this.session.session$.pipe(
    switchMap(() =>
      of(!this.userService.isCurrentUserGlobal())
    )
  );
  protected isKanbanOpen$ = this.kanbanOverlayService.isOpen$;
  protected showPhoneApp$ = this.getShowPhoneappListener()
  private _notificationNumber = 0;

  constructor(
    private userService: UserService,
    private session: SessionService,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    private logger: LoggerService,
    private userNotificationStateService: UserNotificationStateService,
    private responsivenessService: ResponsivenessService,
    private livechatStateService: LiveChatStateService,
    private featureToggleService: FeatureToggleService,
    private readonly themeService: ThemeService,
    private readonly filterService: FilterService,
    private beamerService: BeamerService,
    private socketService: SocketService,
    @Inject(WINDOW_INJECTION_TOKEN) private window: Window,
    private kanbanOverlayService: KanbanOverlayService,
    private pipelineService: PipelineService,
    private companyService: CompanyService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private phoneService: PhoneService,
    private openedByUrlAccess: OpenedByUrlAccessService
  ) {
    this.theme = this.themeService.getTheme();
    this.updateNotificationNumber();
  }

  ngOnInit() {
    const language =
      LANGUAGES.find(
        (language) => language.value == localStorage.getItem('language')
      ) || null;
    this.filterForm = this.formBuilder.group({ language: [language] });
    this.loadUser();
    this.listenToSessionChanges();
    this.restoreKanbanIfNeeded();
    this.listenToThemeChanges();
    this.checkFeatureToggle();
    this.listenToConnectionStatusSync();
  }

  private listenToConnectionStatusSync(): void {
    this.livechatStateService.connectionEstablished$
      .pipe(
        takeUntil(this.destroy$),
        tap((isEstablished) => {
          if (isEstablished && !this.statusSynced) {
            this.statusSynced = true;
            this.cdr.detectChanges();
          }
        })
      )
      .subscribe();

    this.livechatStateService.connectionStatus$
      .pipe(
        takeUntil(this.destroy$),
        tap((status) => {
          if (status && (status === 'online' || status === 'paused')) {
            if (this.currentStatus !== status) {
              this.currentStatus = status as UserStatus;
              this.cdr.detectChanges();
            }
          }
        })
      )
      .subscribe();
  }

  private listenToThemeChanges(): void {
    this.themeService.listenThemeChange().subscribe((theme: 'dark' | 'light') => {
      this.theme = theme;
      this.cdr.detectChanges();
    });
  }

  private checkFeatureToggle(): void {
    this.showNewMenu = true;
  }

  private isConversationLink(): boolean {
    const params = new URLSearchParams(this.window.location.search);
    return params.has('publicId');
  }

  private restoreKanbanIfNeeded(): void {
    if (this.userService.isCurrentUserGlobal()) {
      localStorage.removeItem('kanban_mode_open');
      return;
    }
    if (!this.kanbanOverlayService.wasOpenBeforeReload()) return;
    if (this.isConversationLink()) return;

    const activeId = this.pipelineService.getActivePipelineId();
    const pipelines = this.pipelineService.loadPipelines();
    const pipeline = activeId
      ? this.pipelineService.getPipelineById(activeId)
      : pipelines[0];
    this.kanbanOverlayService.setPipelineName(pipeline?.name ?? null);
    this.kanbanOverlayService.setPipelineLegacyFormat(this.isLegacyPipelineFormat(pipeline));
    this.kanbanOverlayService.open();
  }

  ngAfterViewInit() {
  }

  onProfileMenuClick() {
    this.logger.log(LogEvent.LIVECHAT_OPEN_USER_HEADER);
  }
  
  updateNotificationNumber(): void {
    setTimeout(() => {
      this._notificationNumber = (<any>window)?.Beamer?.notificationNumber || 0;
    }, 3000);
  }

  loadUser() {
    this.user = this.userService.getCurrentUser();
    this.avatar = this.getHumanAvatar(this.user);
  }

  private listenToSessionChanges(): void {
    this.session.session$?.pipe(takeUntil(this.destroy$))
      .subscribe((session) => {
        if (session?.user) {
          this.loadUser();
          this.cdr.detectChanges();
        }
      });
  }

  navigateToPlatform() {
    window.open(environment.externalUrls.platform, '_blank');
    this.logger.log(LogEvent.LIVECHAT_BACK_TO_ADMIN);
  }

  navigateToSettings() {
    window.open('https://admin.asksuite.com/user-settings/profile', '_blank');
  }

  exitSession() {
    this.logger.log(LogEvent.LIVECHAT_LOGOFF);
    this.userNotificationStateService.setUserDeviceStatus(false);
    
    if (this.livechatStateService && this.livechatStateService.currentSocketConnectionId) {
      this.socketService.finishConnection(
        ConnectionType.LiveChat,
        this.livechatStateService.currentSocketConnectionId
      );
      this.socketService.clearConnections();
      this.livechatStateService.resetSocketConnectionId();
    } else {
      this.socketService.clearConnections();
    }
    
    this.session.exit();
  }

  setLanguage(language: Languagei18n) {
    this.logger.log(LogEvent.LIVECHAT_CHANGE_LANGUAGE);
    this.translateService.use(language.toString());
    localStorage.setItem('language', language.toString());
    const langOption = LANGUAGES.find(l => l.value === language);
    if (langOption) {
      this.filterForm.get('language')?.setValue(langOption);
    }
  }

  toggleTheme(): void {
    this.theme = this.theme === 'light' ? 'dark' : 'light';
    this.themeService.setTheme(this.theme);
    this.logger.log(LogEvent.LIVECHAT_DARK_MODE);
  }

  setTheme(theme: 'light' | 'dark'): void {
    this.theme = theme;
    this.themeService.setTheme(theme);
    this.logger.log(LogEvent.LIVECHAT_DARK_MODE);
    this.activeSubmenu = null;
  }

  setStatus(status: UserStatus): void {
    this.activeSubmenu = null;

    const connectionId = this.livechatStateService.currentSocketConnectionId;
    if (!connectionId || this.updatingStatus) return;

    this.logger.log(LogEvent.LIVECHAT_SET_STATUS, { action: status });

    this.updatingStatus = true;
    this.pendingStatus = status;
    this.socketService.updateConnectionStatus(connectionId, status).subscribe({
      next: () => {
        this.currentStatus = status;
      },
      complete: () => {
        this.updatingStatus = false;
        this.pendingStatus = null;
      }
    });
  }

  onSubmenuHover(submenu: SubmenuType): void {
    if (this.submenuLeaveTimeout) {
      clearTimeout(this.submenuLeaveTimeout);
      this.submenuLeaveTimeout = null;
    }
    if (this.activeSubmenu === submenu) return;
    
    if (this.submenuHoverTimeout) {
      clearTimeout(this.submenuHoverTimeout);
    }
    this.submenuHoverTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        this.activeSubmenu = submenu;
        this.cdr.detectChanges();
      });
    }, this.SUBMENU_HOVER_DELAY);
  }

  onSubmenuLeave(submenu: SubmenuType): void {
    if (this.submenuHoverTimeout) {
      clearTimeout(this.submenuHoverTimeout);
      this.submenuHoverTimeout = null;
    }
    this.submenuLeaveTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        if (this.activeSubmenu === submenu) {
          this.activeSubmenu = null;
          this.cdr.detectChanges();
        }
      });
    }, this.SUBMENU_LEAVE_DELAY);
  }

  onHelpCenterClick() {
    this.window.open(
      this.translateService.instant('HELP_CENTER_URL'),
      '_blank'
    );
    this.logger.log(LogEvent.LIVECHAT_OPEN_HELP_CENTER);
  }

  private getHumanAvatar(user: User): Avatar {
    const userAvatar = user?.urlAvatar || '';
    const userName = user?.name || '';
    return {
      name: userName,
      image: userAvatar,
    };
  }

  private getShowPhoneappListener() {
    return this.phoneService.checkShowPhoneApp();
  }

  onHelpFeedbackClick() {
    this.window.open(
      this.translateService.instant('FEEDBACK_URL', { email: this.user?.email }),
      '_blank'
    );
    this.logger.log(LogEvent.LIVECHAT_OPEN_HELP_CENTER);
  }

  getClass() {
    const dropdownElement = document.querySelector('.dropdown-container');
    if (dropdownElement) {
      (dropdownElement as HTMLElement).style.borderRadius = '12px';
    }

  }

  getTooltip(){
    const tooltip=this.translateService.instant('NOTIFICATION.HEADER_NOTIFICATIONS_NEWS_TITLE')+'\n'+this.translateService.instant('NOTIFICATION.HEADER_NOTIFICATIONS_NEWS')
    return tooltip
  }

  toggleKanban(): void {
    if (this.kanbanOverlayService.isOpen) {
      this.logger.log(LogEvent.LIVECHAT_KANBAN_CLOSE);
      this.kanbanOverlayService.close();
      return;
    }
    this.logger.log(LogEvent.LIVECHAT_KANBAN_OPEN);
    if (this.openedByUrlAccess.hasNoAccess) {
      this.openedByUrlAccess.clear();
      this.filterService.resetAndKeepFilters();
    }
    const activeId = this.pipelineService.getActivePipelineId();
    const pipelines = this.pipelineService.loadPipelines();
    const pipeline = activeId
      ? this.pipelineService.getPipelineById(activeId)
      : pipelines[0];
    this.kanbanOverlayService.setPipelineName(pipeline?.name ?? null);
    this.kanbanOverlayService.setPipelineLegacyFormat(this.isLegacyPipelineFormat(pipeline));
    this.kanbanOverlayService.open();
  }

  private isLegacyPipelineFormat(pipeline?: Pipeline | null): boolean {
    if (!pipeline || pipeline.isDefault === true) {
      return false;
    }

    return pipeline.lanes.some((lane, index) => {
      if (index === 0) {
        return false;
      }

      const hasLegacyTags = (lane.tags?.length ?? 0) > 0;
      const hasNewTagCompanies = (lane.tagCompanies?.length ?? 0) > 0;

      return hasLegacyTags && !hasNewTagCompanies;
    });
  }

  private isChristmasSeason(): boolean {
    const today = new Date();
    const isDecember = today.getMonth() === 11;
    return isDecember && today.getDate() >= 1 && today.getDate() <= 25;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
